import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, TrendingUp, TrendingDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { date: today(), kind: "pemasukan", category: "", amount: "", description: "" };
export const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return (v / 1e9).toFixed(1).replace(".0", "") + "M";
  if (v >= 1e6) return (v / 1e6).toFixed(1).replace(".0", "") + "jt";
  if (v >= 1e3) return (v / 1e3).toFixed(0) + "rb";
  return String(v);
};

function bucketTrend(items, period) {
  const map = {};
  items.forEach((t) => {
    const d = new Date(t.date);
    if (isNaN(d)) return;
    let key, label;
    if (period === "bulanan") {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      label = d.toLocaleDateString("id-ID", { month: "short", year: "numeric" });
    } else {
      const ws = new Date(d);
      const dow = (ws.getDay() + 6) % 7; // Monday=0
      ws.setDate(ws.getDate() - dow);
      key = ws.toISOString().slice(0, 10);
      label = ws.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
    }
    if (!map[key]) map[key] = { key, label, pemasukan: 0, pengeluaran: 0 };
    if (t.kind === "pemasukan") map[key].pemasukan += Number(t.amount || 0);
    else map[key].pengeluaran += Number(t.amount || 0);
  });
  return Object.values(map).sort((a, b) => (a.key < b.key ? -1 : 1)).slice(-12);
}

export default function Kas() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ saldo: 0, pemasukan: 0, pengeluaran: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [period, setPeriod] = useState("bulanan");
  const trend = useMemo(() => bucketTrend(items, period), [items, period]);

  const load = async () => {
    const [l, s] = await Promise.all([api.get("/kas"), api.get("/kas/summary")]);
    setItems(l.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.category.trim()) return toast.error("Kategori wajib diisi");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Jumlah harus lebih dari 0");
    await api.post("/kas", { ...form, amount: Number(form.amount) });
    toast.success("Transaksi kas disimpan");
    setOpen(false);
    setForm(empty);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/kas/${id}`);
    toast.success("Transaksi dihapus");
    load();
  };

  const cards = [
    { label: "Saldo Kas", value: summary.saldo, icon: Wallet, color: "text-primary", ring: "border-primary/30 bg-primary/5" },
    { label: "Total Pemasukan", value: summary.pemasukan, icon: TrendingUp, color: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50" },
    { label: "Total Pengeluaran", value: summary.pengeluaran, icon: TrendingDown, color: "text-rose-600", ring: "border-rose-200 bg-rose-50" },
  ];

  return (
    <div className="space-y-8" data-testid="kas-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">Keuangan</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Kas Masjid</h1>
        </div>
        <Button data-testid="kas-new-button" onClick={() => { setForm(empty); setOpen(true); }} className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2">
          <Plus className="h-5 w-5" /> Tambah Transaksi
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4" data-testid="kas-summary">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-xl border p-5 ${c.ring}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{c.label}</p>
              <c.icon className={`h-5 w-5 ${c.color}`} />
            </div>
            <p className={`mt-3 font-display text-2xl font-bold tabular-nums ${c.color}`} data-testid={`kas-${c.label}`}>{rupiah(c.value)}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl p-5" data-testid="kas-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary">Tren Keuangan</p>
            <h2 className="font-display text-lg font-semibold">Pemasukan vs Pengeluaran</h2>
          </div>
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button data-testid="kas-period-mingguan" onClick={() => setPeriod("mingguan")} className={`px-4 py-2 text-sm font-medium ${period === "mingguan" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>Mingguan</button>
            <button data-testid="kas-period-bulanan" onClick={() => setPeriod("bulanan")} className={`px-4 py-2 text-sm font-medium ${period === "bulanan" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>Bulanan</button>
          </div>
        </div>
        {trend.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Belum ada data untuk ditampilkan.</div>
        ) : (
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(150 22% 90%)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(150 10% 40%)" }} tickLine={false} axisLine={{ stroke: "hsl(150 22% 88%)" }} />
                <YAxis tickFormatter={compact} tick={{ fontSize: 12, fill: "hsl(150 10% 40%)" }} tickLine={false} axisLine={false} width={48} />
                <Tooltip formatter={(v, n) => [rupiah(v), n === "pemasukan" ? "Pemasukan" : "Pengeluaran"]} cursor={{ fill: "hsl(150 25% 96%)" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(150 22% 88%)" }} />
                <Legend formatter={(v) => (v === "pemasukan" ? "Pemasukan" : "Pengeluaran")} />
                <Bar dataKey="pemasukan" fill="#16a34a" radius={[6, 6, 0, 0]} maxBarSize={46} />
                <Bar dataKey="pengeluaran" fill="#e11d48" radius={[6, 6, 0, 0]} maxBarSize={46} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl py-16 text-center">
          <Wallet className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">Belum ada transaksi kas.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="kas-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium">Tanggal</th>
                <th className="p-4 font-medium">Kategori</th>
                <th className="p-4 font-medium">Keterangan</th>
                <th className="p-4 font-medium text-right">Jumlah</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((k) => (
                <tr key={k.id} className="border-b border-border/60 last:border-0">
                  <td className="p-4 whitespace-nowrap">{k.date}</td>
                  <td className="p-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded mr-2 ${k.kind === "pemasukan" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{k.kind}</span>
                    {k.category}
                  </td>
                  <td className="p-4 text-muted-foreground">{k.description || "—"}</td>
                  <td className={`p-4 text-right font-semibold tabular-nums ${k.kind === "pemasukan" ? "text-emerald-600" : "text-rose-600"}`}>
                    {k.kind === "pemasukan" ? "+" : "−"} {rupiah(k.amount)}
                  </td>
                  <td className="p-4 text-right">
                    <Button variant="ghost" size="icon" data-testid={`kas-delete-${k.id}`} onClick={() => remove(k.id)} className="text-destructive h-8 w-8">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader>
            <DialogTitle>Tambah Transaksi Kas</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tanggal</Label>
                <Input type="date" data-testid="kas-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 bg-background" />
              </div>
              <div>
                <Label>Jenis</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-2 bg-background" data-testid="kas-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pemasukan">Pemasukan</SelectItem>
                    <SelectItem value="pengeluaran">Pengeluaran</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Kategori</Label>
              <Input data-testid="kas-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 bg-background" placeholder="mis. Infaq Jumat, Listrik" />
            </div>
            <div>
              <Label>Jumlah (Rp)</Label>
              <Input type="number" data-testid="kas-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 bg-background" placeholder="0" />
            </div>
            <div>
              <Label>Keterangan</Label>
              <Input data-testid="kas-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-background" placeholder="opsional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="kas-save-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
